import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useFulfillmentCenter } from 'src/hooks/useFulfillmentCenter'
import { handleOnKeyDown } from 'src/utils/Form'
import {
  selectDataToExecuteInventory, selectDataPosition, selectGoToPosition, selectConfigsToExecuteInventory,
} from '../../InventorySelector'
import { actions } from '../../InventoryState'
import { ExecutingCount } from '../ExecutingCount/ExecutingCount'
import { StartCounting } from '../StartCounting/StartCounting'
import * as S from './FormStartExecution.styles'
import MultipleSelect from 'src/components/MultipleSelect/MultipleSelect'
import { SelectMui } from 'src/components/SelectMui/SelectMui'
import { fixInputSx } from '../../utils'

interface Props {
  inventoryToExecute: any
}

interface InventoryToExecuteFormData {
  badge: string
  floor: number
  addressesTypes: number[]
}

export const FormStartExecution = ({ inventoryToExecute }: Props) => {
  const dispatch = useDispatch()
  const configsToExecuteInventory = useSelector(selectConfigsToExecuteInventory)
  const { badge, floor, addressesTypes } = useSelector(selectDataToExecuteInventory)
  const position = useSelector(selectDataPosition)
  const goToPosition = useSelector(selectGoToPosition)
  const [, setClickStartPosition] = useState(false)

  const [addressesTypesFocused, setAddressesTypesFocused] = useState(false)

  const { fc, validateFc } = useFulfillmentCenter()

  useEffect(() => {
    validateFc()
  }, [validateFc])

  useEffect(() => {
    if (!configsToExecuteInventory) {
      dispatch(actions.getConfigsToExecuteInventory())
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configsToExecuteInventory])

  const floorsOptions = useMemo(
    () => configsToExecuteInventory?.floors || [],
    [configsToExecuteInventory?.floors],
  )

  const addressTypesOptions = useMemo(
    () => configsToExecuteInventory?.addressesTypes || [],
    [configsToExecuteInventory?.addressesTypes],
  )

  const {
    register, trigger, setError, getValues, watch, formState: { errors },
  } = useForm<InventoryToExecuteFormData>({
    defaultValues: {
      badge: badge || '',
      floor: floor || undefined,
      addressesTypes: addressesTypes || [],
    },
  })

  const selectedAddressTypes = watch('addressesTypes')

  const validateDataToExecuteInventory = async (): Promise<boolean> => {
    const validation = await trigger(['floor', 'addressesTypes'])
    return validation
  }

  const setDataToExecuteInventory = (): { floor: number, addressesTypes: number[] } => {
    const floorValue = getValues('floor')
    const addressesTypesValue = getValues('addressesTypes')

    dispatch(actions.setDataToExecuteInventory({
      floor: floorValue,
      addressesTypes: addressesTypesValue,
    }))

    return {
      floor: floorValue,
      addressesTypes: addressesTypesValue,
    }
  }

  const onBadgeSubmit = async () => {
    const validationBadge = await trigger('badge')
    const validationSelects = await validateDataToExecuteInventory()

    if (validationBadge && validationSelects) {
      setDataToExecuteInventory()

      dispatch(
        actions.validateBadgeToExecuteInventory({
          badge: getValues('badge'),
          id: inventoryToExecute.id,
          setError: (message) => setError('badge', { message }),
        }),
      )
    }
  }

  const onClickGetFirstOrSecondPosition = async () => {
    const validation = await validateDataToExecuteInventory()

    if (!validation) {
      return
    }

    const {
      floor: floorValue,
      addressesTypes: addressesTypesValue,
    } = setDataToExecuteInventory()

    dispatch(actions.getPosition({
      badge,
      floor: floorValue,
      addressesTypes: addressesTypesValue,
      id: inventoryToExecute.id,
      isThirdCount: false,
    }))

    dispatch(actions.setGoToPosition(true))
  }

  const onClickGetThirdPosition = async () => {
    const validation = await validateDataToExecuteInventory()

    if (!validation) {
      return
    }

    const {
      floor: floorValue,
      addressesTypes: addressesTypesValue,
    } = setDataToExecuteInventory()

    dispatch(actions.getPosition({
      badge,
      floor: floorValue,
      addressesTypes: addressesTypesValue,
      id: inventoryToExecute.id,
      isThirdCount: true,
    }))

    dispatch(actions.setGoToPosition(true))
  }

  const clearStates = () => {
    dispatch(actions.setClickExecuteInventory(false))
    dispatch(actions.clearState())
    dispatch(actions.getInventoriesToExecute(fc.id))
  }

  if (position.openCount === true) return <ExecutingCount inventoryToExecute={inventoryToExecute} setClickStartPosition={setClickStartPosition} />
  if (goToPosition === true || position.afterFinishPosition === true) return <StartCounting inventoryToExecute={inventoryToExecute} />

  return (
    <S.Wrapper>
      <SelectMui
        sx={{
          height: '3rem !important',
        }}
        formControlSx={{
          ...fixInputSx,
          width: '20rem !important',
        }}
        name='floor'
        inputProps={{
          ...register(
            'floor',
            { required: 'Campo obrigatório' },
          ),
          'data-testid': 'floor-select-test-id',
        }}
        autoFocus
        autoComplete='off'
        id='floor'
        label='Piso'
        variant='outlined'
        options={floorsOptions}
        error={!!errors.floor}
        helperText={errors.floor?.message}
        optionNone={false}
        type='text'
        maxHeight='600px'
        size='medium'
        dataTestId='floor-select-test-id'
        defaultValue={floor || undefined}
      />
      <MultipleSelect
        sx={{
          height: '3rem !important',
        }}
        formControlSx={{
          ...fixInputSx,
          width: '20rem !important',
        }}
        register={register}
        required
        inputName='addressesTypes'
        id='addressesTypes'
        label='Tipo de endereço'
        variant='outlined'
        options={addressTypesOptions}
        errors={errors.addressesTypes}
        type='text'
        maxHeight='600px'
        optionProperty='id'
        size='medium'
        dataTestId='addresses-types-multiple-select-test-id'
        defaultOptions={addressesTypes || []}
        onFocus={() => setAddressesTypesFocused(true)}
        onBlur={() => setAddressesTypesFocused(false)}
        customInputLabelProps={{
          shrink: (selectedAddressTypes && selectedAddressTypes.length > 0) || addressesTypesFocused,
        }}
      />
      <S.CustomTextField
        sx={{ ...fixInputSx }}
        id='badge'
        name='badge'
        type='password'
        autoComplete='off'
        inputProps={{ ...register('badge', { required: 'Campo obrigatório' }), 'data-testid': 'badge-test' }}
        error={!!errors.badge}
        label='badge'
        helperText={errors.badge?.message}
        fullWidth
        size='medium'
        onKeyDown={(e) => handleOnKeyDown(e, '', onBadgeSubmit)}
      />
      <S.ContainerButton>
        <S.CustomButton
          title='Validar crachá/iniciar inventário'
          type='button'
          bgColor='confirm'
          hoverColor='confirmHover'
          letterColor='white'
          onClick={() => onBadgeSubmit()}
          data-testid='validate-badge-test'
        />
        <S.CustomButton
          title='Voltar'
          type='button'
          bgColor='blue'
          hoverColor='darkBlueHover'
          letterColor='white'
          onClick={() => clearStates()}
        />
        <S.CustomButton
          title='primeira/segunda contagem'
          type='button'
          bgColor='orange'
          hoverColor='lightOrange'
          letterColor='white'
          disabled={!badge}
          onClick={() => onClickGetFirstOrSecondPosition()}
          data-testid='first-second-count-test'
        />
        <S.CustomButton
          title='terceira contagem'
          type='button'
          bgColor='orange'
          hoverColor='lightOrange'
          letterColor='white'
          disabled={!badge || !position.enabledThirdCount}
          onClick={() => onClickGetThirdPosition()}
          data-testid='third-count-test'
        />
      </S.ContainerButton>
    </S.Wrapper>
  )
}
